import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  AlertTriangle,
  FileText,
  User,
  Hash,
  Target,
  Globe
} from 'lucide-react'
import Button from '../ui/Button'

const ImportDetailsModal = ({ importData, onClose }) => {
  const { t } = useTranslation()

  console.log('🔍 ImportDetailsModal render:', {
    importData,
    hasImportData: !!importData,
    status: importData?.status,
    import_id: importData?.import_id
  });

  if (!importData) {
    console.log('❌ ImportDetailsModal: No importData provided, returning null');
    return null;
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />
      case 'processing':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-400" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid Date'
      return date.toLocaleString()
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Error formatting date'
    }
  }

  const formatDuration = (startDate, endDate) => {
    try {
      if (!startDate || !endDate) return 'N/A'
      const start = new Date(startDate)
      const end = new Date(endDate)

      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Invalid dates'

      const diffMs = end - start
      if (diffMs < 0) return 'Invalid duration'

      const diffSeconds = Math.floor(diffMs / 1000)
      const diffMinutes = Math.floor(diffSeconds / 60)

      if (diffMinutes < 1) return `${diffSeconds}s`
      if (diffMinutes < 60) return `${diffMinutes}m ${diffSeconds % 60}s`

      const diffHours = Math.floor(diffMinutes / 60)
      return `${diffHours}h ${diffMinutes % 60}m`
    } catch (error) {
      console.error('Error calculating duration:', startDate, endDate, error);
      return 'Error calculating duration'
    }
  }

  try {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="import-details-modal-overlay">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-testid="import-details-modal">
        {/* Enhanced Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full bg-white dark:bg-gray-800 shadow-sm">
              {getStatusIcon(importData.status)}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {t('tms.importDetails')}
              </h3>
              <div>
                <span className={`px-3 py-1 text-sm rounded-full font-medium ${getStatusColor(importData.status)}`}>
                  {t(`tms.status.${importData.status}`)}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-white/80 dark:hover:bg-gray-600 rounded-full transition-colors group"
          >
            <X className="w-5 h-5 text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status and Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(importData.status)}
                  <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(importData.status)}`}>
                    {t(`tms.status.${importData.status}`)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Project
                </label>
                <div className="text-sm text-gray-900 dark:text-white space-y-1">
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-2 mt-0.5">Name:</span>
                    <span className="font-medium">{importData?.project_name || 'Unknown Project'}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-2 mt-0.5">ID:</span>
                    <span className="text-xs font-mono">{importData?.project_id || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Translation Job
                </label>
                <div className="text-sm text-gray-900 dark:text-white font-mono">
                  {importData?.job_uuid || 'N/A'}
                </div>
              </div>

              {/* Upload ID (if exists) */}
              {importData?.statistics?.upload_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <Target className="w-4 h-4 inline mr-1" />
                    Upload ID
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white font-mono">
                    {importData.statistics.upload_id}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Started At
                </label>
                <div className="text-sm text-gray-900 dark:text-white">
                  {formatDate(importData.started_at)}
                </div>
              </div>

              {importData?.completed_at && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Completed At
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {formatDate(importData?.completed_at)}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Duration
                </label>
                <div className="text-sm text-gray-900 dark:text-white">
                  {formatDuration(importData.started_at, importData.completed_at)}
                </div>
              </div>

              {(importData?.retry_count || 0) > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Retries
                  </label>
                  <div className="text-sm text-orange-600 dark:text-orange-400">
                    {importData?.retry_count} {importData?.retry_count === 1 ? 'retry' : 'retries'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Statistics */}
          {importData?.statistics && Object.keys(importData.statistics).length > 0 && (
            <div data-testid="import-statistics">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Import Statistics</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="statistics-grid">
                {Object.entries(importData.statistics).map(([key, value]) => {
                  // Skip complex nested objects that can't be rendered directly
                  if (typeof value === 'object' && value !== null) {
                    console.log(`⏭️ Skipping complex object key: ${key}`, typeof value);
                    return null;
                  }

                  // Skip upload_id since it's now in the status section
                  if (key === 'upload_id') {
                    return null;
                  }

                  // Only render primitive values (numbers, strings, booleans)
                  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
                    const isNumber = typeof value === 'number';
                    const displayValue = isNumber ? value.toLocaleString() : String(value);

                    // Choose colors based on key names for better visual hierarchy
                    let cardColor = 'from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20';
                    let numberColor = 'text-blue-600 dark:text-blue-400';

                    if (key.includes('error') || key.includes('failed')) {
                      cardColor = 'from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20';
                      numberColor = 'text-red-600 dark:text-red-400';
                    } else if (key.includes('processed') || key.includes('created') || key.includes('updated')) {
                      cardColor = 'from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20';
                      numberColor = 'text-green-600 dark:text-green-400';
                    } else if (key.includes('upload')) {
                      cardColor = 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20';
                      numberColor = 'text-purple-600 dark:text-purple-400';
                    }

                    return (
                      <div
                        key={key}
                        data-testid={`statistic-${key}`}
                        data-value={displayValue}
                        className={`bg-gradient-to-br ${cardColor} rounded-xl p-4 border border-gray-200/50 dark:border-gray-600/50 transition-transform hover:scale-105`}
                      >
                        <div className={`text-2xl font-bold ${numberColor} mb-1`}>
                          {displayValue}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 font-medium capitalize leading-tight">
                          {key.replace(/_/g, ' ')}
                        </div>
                      </div>
                    );
                  }

                  console.log(`⏭️ Skipping non-primitive value for key: ${key}`, typeof value);
                  return null;
                })}
              </div>
            </div>
          )}

          {/* Enhanced Error Message */}
          {importData?.error_message && (
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-1 h-6 bg-gradient-to-b from-red-500 to-red-600 rounded-full"></div>
                <h4 className="text-lg font-semibold text-red-700 dark:text-red-400">Error Details</h4>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-l-4 border-red-500 rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-red-800 dark:text-red-200 font-medium leading-relaxed">
                      {importData.error_message}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Import Settings */}
          {(importData.import_settings || importData.settings_applied) && Object.keys(importData.import_settings || importData.settings_applied || {}).length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full"></div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Import Configuration</h4>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-gray-200/50 dark:border-gray-600/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(importData.import_settings || importData.settings_applied || {}).map(([key, value]) => {
                    // Skip API key for security reasons
                    if (key.toLowerCase().includes('api_key')) {
                      return null;
                    }

                    let displayValue = value;
                    if (typeof value === 'boolean') {
                      displayValue = value ? '✓ Enabled' : '✗ Disabled';
                    } else if (Array.isArray(value)) {
                      displayValue = value.join(', ');
                    } else {
                      displayValue = String(value);
                    }

                    return (
                      <div key={key} className="flex flex-col space-y-1">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm text-gray-900 dark:text-white font-medium">
                          {displayValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Metadata */}
          {importData.metadata && Object.keys(importData.metadata).length > 0 && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Additional Metadata</h4>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="space-y-2">
                  {Object.entries(importData.metadata).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-start text-sm">
                      <span className="text-gray-600 dark:text-gray-400 capitalize min-w-0 mr-4">
                        {key.replace(/_/g, ' ')}:
                      </span>
                      <span className="text-gray-900 dark:text-white text-right">
                        {typeof value === 'object' ? (
                          <div className="space-y-1">
                            {Object.entries(value).map(([subKey, subValue]) => (
                              <div key={subKey} className="text-xs">
                                <span className="text-gray-500">{subKey}:</span> {String(subValue)}
                              </div>
                            ))}
                          </div>
                        ) : (
                          String(value)
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Import completed {importData.completed_at ? formatDate(importData.completed_at) : formatDate(importData.started_at)}
          </div>
          <Button
            onClick={onClose}
            variant="primary"
            className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
          >
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
    )
  } catch (error) {
    console.error('❌ Error rendering ImportDetailsModal:', error);
    console.error('ImportData that caused error:', importData);

    // Return a simple error modal
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-red-600">Error Loading Import Details</h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            There was an error displaying the import details. Please try again.
          </p>
          <div className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 p-2 rounded mb-4">
            Error: {error?.message}
          </div>
          <button
            onClick={onClose}
            className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }
}

export default ImportDetailsModal